// Pure-logic helpers for TopNav.
//
// Kept in a plain .ts file (no JSX) so the node-pool test suite can
// import them without the root tsconfig needing JSX support. The
// TopNav component (TopNav.tsx) re-exports these so web code keeps
// the single-import ergonomics.

import { canSeeAutomationsNav, type RoleAwareUser } from "../utils/role-visibility";

export interface NavItem {
  label: string;
  to: string;
  // When set, the item is only included by `visibleNavItems` for users
  // who pass the matching predicate. Kept as a discriminator string
  // (rather than an inlined function) so the array can stay a
  // serializable `readonly` constant — easier to test, easier to
  // diff.
  visibility?: "admin_only";
  // Key into CORE_VOCABULARY (core-vocabulary.ts) for the inline "what
  // is this?" `?` affordance. A plain string (not the definition text)
  // so this array stays serializable and the definitions live in one
  // place. Omit to render no help glyph.
  help?: string;
}

// Routes rendered in the primary nav. Order matches the expected
// read-order for advisors: daily view first, then record-centric
// views, then workflow surfaces, then admin/communications/reports.
//
// Vocabulary v1.4 mapping (per `vocabulary-system.md` Decision A
// as revised 2026-04-29):
//   Opportunities → opportunities table → /opportunities
//   Processing    → transactions table  → /processing
//   Ongoing Care  → rhythm_instances    → /ongoing-care
//   Automations   → automations table   → /automations  (admin-only)
//
// "Sales" / "In Progress" — the v1.0–v1.3 user-facing labels — are
// retired here. App.tsx 302-redirects /sales → /opportunities and
// /in-progress → /processing so any old bookmarks land somewhere
// reasonable. Same goes for the older /deals and /pipelines
// placeholders.
// Top nav is deliberately minimal — it surfaces the five primary
// record/workflow surfaces every advisor uses daily and nothing else.
// What's NOT here, and why:
//   * Dashboard — reachable via the logo click (logo → "/" → /dashboard
//     redirect) and via the left-pane Sidebar (see sidebar-data.ts).
//     Removed from top nav 2026-05-24 (audit-sweep PR F, finding #11).
//   * Communications — not a near-term build. /communications route
//     stays wired in App.tsx as a stub so legacy bookmarks don't 404,
//     but it isn't advertised. Removed 2026-05-24.
//   * Automations — admin plumbing, lives in the sidebar's ADMIN
//     section. Removed from top nav 2026-05-25 per Grant's
//     "top menu is too cluttered" call.
//   * Reports — reachable via the sidebar's role-specific shortcut
//     rail (non-admin advisors + Brandon already had it there) AND
//     via the ADMIN section for founder + compliance. Removed from
//     top nav 2026-05-25 in the same cleanup.
// ORDER + help glyphs set by Grant, 2026-07-25: "remove the question mark
// tool tips out of the top menu next to contacts, households, services,
// opportunities, and processing. also change the order of the menu to
// contacts, households, services, processing, opportunities, forms."
// Revised 2026-08-06: "Change it to Contacts, Households, Services,
// Opportunities, Processing, Ongoing Care, Forms. And remove the tool
// tip question marks from ongoing care and forms."
// Revised 2026-08-23: Forms removed entirely (see the note on the item
// that used to sit at the end of the list).
//
// So Opportunities sits BEFORE Processing (work being won reads ahead of
// work in flight), and no `?` help glyphs remain anywhere in the top
// menu — including on the one OS-vocabulary label still here, Ongoing
// Care.
export const NAV_ITEMS: readonly NavItem[] = [
  { label: "Contacts", to: "/contacts" },
  { label: "Households", to: "/households" },
  // Services — the service-records surface (4,017 live records, Joy's daily
  // page) promoted from the sidebar's CLIENTS group to the top bar per the
  // approved nav map (Grant, 2026-07-24). Labeled "Services" (not "Policies &
  // Services") — "we are a financial services company". The rail's duplicate
  // is removed in the same change so it lives in exactly one place.
  { label: "Services", to: "/service-records" },
  { label: "Opportunities", to: "/opportunities" },
  { label: "Processing", to: "/processing" },
  { label: "Ongoing Care", to: "/ongoing-care" },
  // Forms LEFT the top bar 2026-08-23 (Grant: "from the top navigation
  // remove FORMS from it. That needs to go into the left hand pane
  // navigation"). It now heads its own left-pane section, alongside
  // E-sign. The six that remain are the record lanes an advisor moves
  // between all day; Forms is a build-and-manage surface, which reads
  // better in the rail. Six items also fit without the "More" overflow
  // menu at every realistic window width.
] as const;

// Exported for unit tests — the scroll threshold where the glass
// state kicks in.
export const GLASS_SCROLL_THRESHOLD = 80;

// Filter NAV_ITEMS down to what the supplied user is allowed to see.
// Today this only differs for the admin_only visibility flag (covers
// Automations); future flags can extend the predicate without
// touching call sites.
export function visibleNavItems(
  user: RoleAwareUser,
  items: readonly NavItem[] = NAV_ITEMS,
): NavItem[] {
  // Ambassador Agents (deny-by-default role): every standard lane
  // (Contacts, Households, Services, …) 403s for them server-side, so
  // the bar shows their one real destination instead of seven dead
  // links. UX only — the restricted-role route gate is the boundary.
  if (user?.employee_role_slug === "ambassador") {
    return [{ label: "Referrals", to: "/referrals" }];
  }
  return items.filter((item) => {
    if (item.visibility === "admin_only") {
      return canSeeAutomationsNav(user);
    }
    return true;
  });
}

// Given a pathname, return which top-level nav item it matches (or
// null). A route matches a nav item when the pathname is the item's
// `to` OR is a descendant of it. "/households" matches both
// `/households` and `/households/abc123`. The root path does not
// match anything here because it's handled by the redirect upstream.
//
// Match operates on the full NAV_ITEMS list (not just the visible
// subset) so deep-linking to an admin-only path still highlights the
// correct nav item if the role check changes mid-session.
export function activeNavItemFor(
  pathname: string,
  items: readonly NavItem[] = NAV_ITEMS,
): NavItem | null {
  // Sort descending by length so `/households/x/y` matches
  // `/households` even if another item starts with `/h`. We match on
  // exact OR prefix followed by `/`.
  const sorted = [...items].sort((a, b) => b.to.length - a.to.length);
  for (const item of sorted) {
    if (pathname === item.to) return item;
    if (pathname.startsWith(item.to + "/")) return item;
  }
  return null;
}

// Turns a display name like "Grant Botma" into "GB"; falls back to
// the first two letters of the email local part when name is missing.
export function initialsFor(user: { name: string | null; email: string }): string {
  const source = (user.name ?? user.email.split("@")[0] ?? "").trim();
  if (!source) return "?";
  const words = source.split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) {
    const single = words[0]!;
    return (single.slice(0, 2) || "?").toUpperCase();
  }
  const first = words[0]!.charAt(0);
  const last = words[words.length - 1]!.charAt(0);
  return (first + last).toUpperCase();
}

// Maps an employee_role_slug to the user-friendly label advisors
// actually say out loud.
export function displayRoleLabel(slug: string | null): string {
  switch (slug) {
    case "system_admin":
      return "System admin";
    case "company_admin":
      return "Company admin";
    case "advisor":
      return "Advisor";
    case "front_desk":
      return "Front desk";
    case "compliance":
      return "Compliance";
    case "back_office":
      return "Back office";
    case "ambassador":
      return "Ambassador Agent";
    default:
      return slug ?? "";
  }
}

/**
 * How many primary-nav items fit in `availablePx`.
 *
 * When everything fits, returns them all and no "More" trigger renders.
 * When it doesn't, reserves `moreTriggerPx` for that trigger and returns
 * how many still fit beside it — the rest move INTO the menu, so nothing
 * is ever sliced mid-word or stranded behind an invisible scroll
 * (Grant, 2026-07-25).
 *
 * `itemWidthsPx` are measured natural widths (each already including the
 * flex gap). A non-positive `availablePx` means "not laid out yet"
 * (jsdom, pre-paint) — show everything rather than collapsing the nav.
 */
export function visibleNavCount(
  itemWidthsPx: number[],
  availablePx: number,
  moreTriggerPx: number,
): number {
  if (itemWidthsPx.length === 0) return 0;
  if (availablePx <= 0) return itemWidthsPx.length;
  const total = itemWidthsPx.reduce((sum, w) => sum + w, 0);
  if (total <= availablePx) return itemWidthsPx.length;
  let used = 0;
  let count = 0;
  for (const w of itemWidthsPx) {
    if (used + w + moreTriggerPx > availablePx) break;
    used += w;
    count += 1;
  }
  return count;
}

/**
 * A `===`-stable fingerprint of an item list's ROUTES, independent of
 * array identity. Used as a `useLayoutEffect` dependency in
 * TopNavPrimaryNav so overflow re-measurement is keyed on the items
 * actually changing, not on the caller handing back a new array
 * instance with the same content (Sentry JAVASCRIPT-REACT-3N,
 * 2026-08-27 — an unmemoized `visibleNavItems()` call in TopNav
 * produced a fresh array every render, which kept resetting the
 * measured widths and re-triggering the overflow fit, forever).
 */
export function navItemsSignature(items: readonly NavItem[]): string {
  return items.map((item) => item.to).join("|");
}
